import {
	Fn,
	If,
	float,
	floor,
	fract,
	hash,
	min,
	mix,
	smoothstep,
	texture3D,
	vec2,
	vec3,
	vec4
} from 'three/tsl';

const valueNoise2D = Fn( ( [ coordinate, seed ] ) => {

	const cell = floor( coordinate );
	const local = fract( coordinate );
	const blend = local.mul( local ).mul( float( 3 ).sub( local.mul( 2 ) ) );
	const cellSeed = cell.x.add( cell.y.mul( 157.31 ) ).add( seed.mul( 19.19 ) );
	const a = hash( cellSeed );
	const b = hash( cellSeed.add( 1 ) );
	const c = hash( cellSeed.add( 157.31 ) );
	const d = hash( cellSeed.add( 158.31 ) );

	return mix( mix( a, b, blend.x ), mix( c, d, blend.x ), blend.y );

} ).setLayout( {
	name: 'cyborgCloudValueNoise2D',
	type: 'float',
	inputs: [
		{ name: 'coordinate', type: 'vec2' },
		{ name: 'seed', type: 'float' }
	]
} );

export function createCloudDensitySampler( baseTexture, detailTexture, uniforms ) {

	const baseField = texture3D( baseTexture, null, 0 );
	const detailField = texture3D( detailTexture, null, 0 );

	return Fn( ( [ position, detailLod ] ) => {

		const height = float( position.y.add( 0.5 ) ).toVar();
		const bottom = smoothstep( 0, uniforms.bottomFade, height );
		const top = smoothstep( 0, uniforms.topFade, height.oneMinus() );
		const heightProfile = bottom.mul( top ).clamp();
		const worldXZ = position.xz.mul( uniforms.volumeSize.xz ).add( uniforms.volumePosition.xz );
		const weatherCoordinate = worldXZ.div( uniforms.weatherScale );
		const weatherA = valueNoise2D( weatherCoordinate, uniforms.patternSeed );
		const weatherB = valueNoise2D(
			weatherCoordinate.mul( 2.037 ).add( vec2( 13.17, - 7.31 ) ),
			uniforms.patternSeed.add( 11.7 )
		);
		const weather = weatherA.mul( 0.68 ).add( weatherB.mul( 0.32 ) );
		const warpCoordinate = worldXZ.div( uniforms.warpScale ).add( uniforms.warpOffset );
		const warp = vec2(
			valueNoise2D( warpCoordinate.add( vec2( 5.17, 19.31 ) ), uniforms.patternSeed.add( 23.1 ) ),
			valueNoise2D( warpCoordinate.add( vec2( - 17.7, 3.11 ) ), uniforms.patternSeed.add( 47.3 ) )
		).mul( 2 ).sub( 1 ).mul( uniforms.warpStrength );
		const worldPattern = worldXZ.div( uniforms.worldScale ).add( warp );
		const baseCoordinate = vec3(
			worldPattern.x.mul( uniforms.shapeScale ).add( uniforms.windOffset.x ),
			height.mul( 0.92 ).add( 0.04 ),
			worldPattern.y.mul( uniforms.shapeScale ).add( uniforms.windOffset.y )
		);
		const decorrelatedBaseXZ = vec2(
			baseCoordinate.x.mul( 0.8 ).sub( baseCoordinate.z.mul( 0.6 ) ),
			baseCoordinate.x.mul( 0.6 ).add( baseCoordinate.z.mul( 0.8 ) )
		).mul( 1.371 ).add( vec2( 0.173, 0.619 ) );
		const primaryBaseSample = baseField.sample( baseCoordinate );
		const secondaryBaseSample = baseField.sample( vec3(
			decorrelatedBaseXZ.x,
			baseCoordinate.y.mul( 1.071 ).add( 0.137 ),
			decorrelatedBaseXZ.y
		) );
		const baseSample = mix( primaryBaseSample, secondaryBaseSample, uniforms.baseBlend );
		const columnSample = baseField.sample( vec3( baseCoordinate.x, 0.23, baseCoordinate.z ) );
		const baseShape = baseSample.dot( uniforms.baseWeights )
			.add( vec2( columnSample.r, columnSample.a ).dot( uniforms.columnWeights ) )
			.add( uniforms.formationBias )
			.add( weather.sub( 0.5 ).mul( uniforms.weatherStrength ).mul( 0.38 ) );
		const anvilBias = smoothstep( 0.42, 0.9, height ).mul( uniforms.anvil ).mul( 0.11 );
		const heightPenalty = heightProfile.oneMinus().mul( 0.58 );
		const shaped = baseShape.add( anvilBias ).sub( heightPenalty );
		const threshold = float( 0.94 ).sub( uniforms.coverage.mul( 0.9 ) );
		const baseDensity = smoothstep( threshold, threshold.add( uniforms.softness ), shaped ).toVar();

		const filteredDetail = mix( 1, detailLod, uniforms.detailFilter ).clamp();
		const localRoil = vec2( baseSample.g.sub( 0.5 ), baseSample.b.sub( 0.5 ) );
		const roilWarp = vec2(
			localRoil.x.mul( uniforms.roilVector.x ).sub( localRoil.y.mul( uniforms.roilVector.y ) ),
			localRoil.x.mul( uniforms.roilVector.y ).add( localRoil.y.mul( uniforms.roilVector.x ) )
		).mul( 0.28 );
		const detailPattern = worldPattern.add( roilWarp );
		const detailCoordinate = vec3(
			detailPattern.x.mul( uniforms.shapeScale ).mul( uniforms.detailScale ).add( uniforms.detailOffset.x ),
			height.mul( uniforms.detailScale.mul( 0.72 ) ).add( uniforms.detailOffset.y ),
			detailPattern.y.mul( uniforms.shapeScale ).mul( uniforms.detailScale ).add( uniforms.detailOffset.z )
		);
		const decorrelatedDetailXZ = vec2(
			detailCoordinate.x.mul( 0.384 ).sub( detailCoordinate.z.mul( 0.923 ) ),
			detailCoordinate.x.mul( 0.923 ).add( detailCoordinate.z.mul( 0.384 ) )
		).mul( 1.173 ).add( vec2( 0.417, 0.281 ) );
		const primaryDetailSample = detailField.sample( detailCoordinate );
		const secondaryDetailSample = detailField.sample( vec3(
			decorrelatedDetailXZ.x,
			detailCoordinate.y.mul( 0.937 ).add( 0.271 ),
			decorrelatedDetailXZ.y
		) );
		const detailSample = mix( primaryDetailSample, secondaryDetailSample, uniforms.detailBlend );
		const detailValue = float( detailSample.dot( uniforms.detailWeights )
			.add( baseSample.g.sub( baseSample.a ).mul( uniforms.roilModulation ) ) ).toVar();
		const detailWeight = float( 1 ).toVar();

		If( uniforms.detailOctaves.greaterThan( 1.5 ), () => {

			const octaveWeight = filteredDetail.mul( 0.32 );
			const octave = detailField.sample( detailCoordinate.mul( 1.93 ).add( vec3( 0.17, 0.31, 0.53 ) ) );
			detailValue.addAssign( octave.g.mul( octaveWeight ) );
			detailWeight.addAssign( octaveWeight );

		} );

		If( uniforms.detailOctaves.greaterThan( 2.5 ), () => {

			const octaveWeight = filteredDetail.mul( 0.16 );
			const octave = detailField.sample( detailCoordinate.mul( 3.87 ).add( vec3( 0.61, 0.11, 0.37 ) ) );
			detailValue.addAssign( octave.b.mul( octaveWeight ) );
			detailWeight.addAssign( octaveWeight );

		} );

		const normalizedDetail = detailValue.div( detailWeight ).sub( 0.5 )
			.mul( uniforms.detailContrast ).add( 0.5 ).clamp();
		const erosionLod = mix( 0.42, 1, filteredDetail );
		const erosion = normalizedDetail.oneMinus().mul( uniforms.detailErosion ).mul( erosionLod )
			.mul( float( 0.32 ).add( baseDensity.oneMinus().mul( 0.68 ) ) );
		const edgeX = min( position.x.add( 0.5 ), float( 0.5 ).sub( position.x ) );
		const edgeZ = min( position.z.add( 0.5 ), float( 0.5 ).sub( position.z ) );
		const boundary = smoothstep( 0, uniforms.boundaryFade, min( edgeX, edgeZ ) );
		const density = baseDensity.sub( erosion ).clamp().mul( uniforms.density ).mul( heightProfile ).mul( boundary );

		return vec4( density, baseDensity, normalizedDetail, heightProfile );

	} ).setLayout( {
		name: 'sampleCyborgCloudDensity',
		type: 'vec4',
		inputs: [
			{ name: 'position', type: 'vec3' },
			{ name: 'detailLod', type: 'float' }
		]
	} );

}
